"use strict";
// Copyright (C) 2017-2023 Smart code 203358507
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
var react_1 = require("react");
var react_i18next_1 = require("react-i18next");
var fast_equals_1 = require("fast-equals");
var core_1 = require("stremio/core");
var useCacheLocation_1 = __importDefault(require("./useCacheLocation"));
var CACHE_SIZES = [0, 2147483648, 5368709120, 10737418240, 53687091200, 107374182400, 214748364800, null];
var cacheSizeToString = function (size) {
    return size === null ?
        'Infinite'
        :
            size === 0 ?
                'No caching'
                :
                    "".concat(Math.ceil(((size / 1024 / 1024 / 1024) + Number.EPSILON) * 100) / 100, "GiB");
};
var TORRENT_PROFILES = {
    default: {
        btDownloadSpeedHardLimit: 3670016,
        btDownloadSpeedSoftLimit: 2621440,
        btHandshakeTimeout: 20000,
        btMaxConnections: 55,
        btMinPeersForStable: 5,
        btRequestTimeout: 4000
    },
    soft: {
        btDownloadSpeedHardLimit: 1677721.6,
        btDownloadSpeedSoftLimit: 1677721.6,
        btHandshakeTimeout: 20000,
        btMaxConnections: 35,
        btMinPeersForStable: 5,
        btRequestTimeout: 4000
    },
    fast: {
        btDownloadSpeedHardLimit: 39321600,
        btDownloadSpeedSoftLimit: 4194304,
        btHandshakeTimeout: 20000,
        btMaxConnections: 200,
        btMinPeersForStable: 10,
        btRequestTimeout: 4000
    },
    'ultra fast': {
        btDownloadSpeedHardLimit: 78643200,
        btDownloadSpeedSoftLimit: 8388608,
        btHandshakeTimeout: 25000,
        btMaxConnections: 400,
        btMinPeersForStable: 10,
        btRequestTimeout: 6000
    }
};
var useStreamingOptions = function (streamingServer) {
    var core = (0, core_1.useCore)();
    var t = (0, react_i18next_1.useTranslation)().t;
    var cacheLocation = (0, useCacheLocation_1.default)(streamingServer);
    // TODO combine those useMemo in one
    var settings = (0, react_1.useMemo)(function () {
        var _a;
        return (((_a = streamingServer === null || streamingServer === void 0 ? void 0 : streamingServer.settings) === null || _a === void 0 ? void 0 : _a.type) === 'Ready' ?
            streamingServer.settings.content : null);
    }, [streamingServer.settings]);
    var networkInfo = (0, react_1.useMemo)(function () {
        var _a;
        return (((_a = streamingServer === null || streamingServer === void 0 ? void 0 : streamingServer.networkInfo) === null || _a === void 0 ? void 0 : _a.type) === 'Ready' ?
            streamingServer.networkInfo.content : null);
    }, [streamingServer.networkInfo]);
    var deviceInfo = (0, react_1.useMemo)(function () {
        var _a;
        return (((_a = streamingServer === null || streamingServer === void 0 ? void 0 : streamingServer.deviceInfo) === null || _a === void 0 ? void 0 : _a.type) === 'Ready' ?
            streamingServer.deviceInfo.content : null);
    }, [streamingServer.deviceInfo]);
    var streamingServerRemoteUrlInput = (0, react_1.useMemo)(function () { return ({
        value: streamingServer.remoteUrl,
    }); }, [streamingServer.remoteUrl]);
    var remoteEndpointSelect = (0, react_1.useMemo)(function () {
        if (!settings || !networkInfo) {
            return null;
        }
        return {
            options: __spreadArray([
                {
                    label: t('SETTINGS_DISABLED'),
                    value: '',
                }
            ], networkInfo.availableInterfaces.map(function (address) { return ({
                label: address,
                value: address,
            }); }), true),
            value: settings.remoteHttps,
            onSelect: function (value) {
                core.transport.dispatch({
                    action: 'StreamingServer',
                    args: {
                        action: 'UpdateSettings',
                        args: __assign(__assign({}, settings), { remoteHttps: value })
                    }
                });
            }
        };
    }, [settings, networkInfo]);
    var cacheSizeSelect = (0, react_1.useMemo)(function () {
        if (!settings) {
            return null;
        }
        return {
            options: CACHE_SIZES.map(function (size) { return ({
                label: cacheSizeToString(size),
                value: JSON.stringify(size)
            }); }),
            value: JSON.stringify(settings.cacheSize),
            title: function () {
                return cacheSizeToString(settings.cacheSize);
            },
            onSelect: function (value) {
                core.transport.dispatch({
                    action: 'StreamingServer',
                    args: {
                        action: 'UpdateSettings',
                        args: __assign(__assign({}, settings), { cacheSize: JSON.parse(value) })
                    }
                });
            }
        };
    }, [settings]);
    var torrentProfileSelect = (0, react_1.useMemo)(function () {
        if (!settings) {
            return null;
        }
        var selectedTorrentProfile = {
            btDownloadSpeedHardLimit: settings.btDownloadSpeedHardLimit,
            btDownloadSpeedSoftLimit: settings.btDownloadSpeedSoftLimit,
            btHandshakeTimeout: settings.btHandshakeTimeout,
            btMaxConnections: settings.btMaxConnections,
            btMinPeersForStable: settings.btMinPeersForStable,
            btRequestTimeout: settings.btRequestTimeout
        };
        var isCustomTorrentProfileSelected = Object.values(TORRENT_PROFILES).every(function (torrentProfile) {
            return !(0, fast_equals_1.deepEqual)(torrentProfile, selectedTorrentProfile);
        });
        return {
            options: Object.keys(TORRENT_PROFILES)
                .map(function (profileName) { return ({
                label: t('TORRENT_PROFILE_' + profileName.replace(' ', '_').toUpperCase()),
                value: JSON.stringify(TORRENT_PROFILES[profileName])
            }); })
                .concat(isCustomTorrentProfileSelected ?
                [{
                        label: 'custom',
                        value: JSON.stringify(selectedTorrentProfile)
                    }]
                :
                    []),
            value: JSON.stringify(selectedTorrentProfile),
            onSelect: function (value) {
                core.transport.dispatch({
                    action: 'StreamingServer',
                    args: {
                        action: 'UpdateSettings',
                        args: __assign(__assign({}, settings), JSON.parse(value))
                    }
                });
            }
        };
    }, [settings]);
    var transcodingProfileSelect = (0, react_1.useMemo)(function () {
        if (!settings || !deviceInfo) {
            return null;
        }
        return {
            options: __spreadArray([
                {
                    label: t('SETTINGS_DISABLED'),
                    value: null,
                }
            ], deviceInfo.availableHardwareAccelerations.map(function (name) { return ({
                label: name,
                value: name,
            }); }), true),
            value: settings.transcodeProfile,
            onSelect: function (value) {
                core.transport.dispatch({
                    action: 'StreamingServer',
                    args: {
                        action: 'UpdateSettings',
                        args: __assign(__assign({}, settings), { transcodeProfile: value })
                    }
                });
            }
        };
    }, [settings, deviceInfo]);
    return {
        streamingServerRemoteUrlInput: streamingServerRemoteUrlInput,
        remoteEndpointSelect: remoteEndpointSelect,
        cacheSizeSelect: cacheSizeSelect,
        cacheLocationSelect: cacheLocation.select,
        cacheLocationError: cacheLocation.error,
        settingsDisabled: cacheLocation.busy,
        torrentProfileSelect: torrentProfileSelect,
        transcodingProfileSelect: transcodingProfileSelect,
    };
};
exports.default = useStreamingOptions;
